import { Home, Zap, Car, Monitor, Leaf, Shield, Snowflake, Sparkles, Globe, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useBusinessMode, businessModeContent } from "@/hooks/use-business-mode";
import { AnimatePresence, motion } from "framer-motion";
import gartenHero from "@/assets/garten-hero.jpg.asset.json";
import hausmeisterHero from "@/assets/hausmeister-hero.jpg.asset.json";
import reinigungHero from "@/assets/reinigung-hero.png.asset.json";

type Svc = { icon: typeof Home; title: string; description: string; to: string; span: string; bg?: string; img?: string };

const facilityServices: Svc[] = [
  { icon: Home, title: "Hausmeisterservice", description: "Wartung, Instandhaltung und Gebäudebetreuung – privat und gewerblich.", to: "/hausmeister", span: "lg:col-span-6 lg:row-span-2", img: hausmeisterHero.url },
  { icon: Zap, title: "Elektrotechnik", description: "Installationen, Reparaturen und Notdienst in Rhein-Main.", to: "/elektrotechnik", span: "lg:col-span-3", bg: "bg-butter" },
  { icon: Car, title: "Car Detailing", description: "Außenpflege, Innenraum und Lackversiegelung.", to: "/car-detailing", span: "lg:col-span-3", bg: "bg-sky" },
  { icon: Snowflake, title: "Winterdienst", description: "Schneeräumung und Streudienst für sichere Wege.", to: "/winterdienst", span: "lg:col-span-3", bg: "bg-lavender" },
  { icon: Shield, title: "Security Service", description: "Objektschutz und Veranstaltungssicherheit.", to: "/security-service", span: "lg:col-span-3", bg: "bg-rose" },
  { icon: Leaf, title: "Gartenarbeiten", description: "Rasen, Hecke und Saisonpflege.", to: "/gartenarbeiten", span: "lg:col-span-6", img: gartenHero.url },
  { icon: Sparkles, title: "Reinigungsservice", description: "Büro, Treppenhaus und Grundreinigung.", to: "/reinigungsservice", span: "lg:col-span-6", img: reinigungHero.url },
];

const digitalServices: Svc[] = [
  { icon: Globe, title: "Website Erstellung", description: "Moderne Websites ab 449 €.", to: "/website-erstellung", span: "lg:col-span-6", bg: "bg-lavender" },
  { icon: Monitor, title: "IT Helpdesk", description: "IT Support remote und vor Ort.", to: "/it-helpdesk", span: "lg:col-span-6", bg: "bg-sky" },
];

const ServicesSection = () => {
  const { mode } = useBusinessMode();
  const content = businessModeContent[mode];
  const services = mode === "facility" ? facilityServices : digitalServices;

  return (
    <section id="services" className="py-12 md:py-20 bg-background">
      <div className="container mx-auto px-4">
        <AnimatePresence mode="wait">
          <motion.div key={mode} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
            <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-end md:justify-between gap-3 md:gap-4 mb-6 md:mb-10">
              <div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3">{content.sectionTitle}</h2>
                <p className="text-lg text-muted-foreground max-w-2xl">{content.sectionSubtitle}</p>
              </div>
              <Link to="/dienstleistungen" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline shrink-0">
                Alle Leistungen <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 md:gap-5 max-w-6xl mx-auto lg:auto-rows-[190px]">
              {services.map((s) =>
                s.img ? (
                  <Link key={s.title} to={s.to} className={`${s.span} relative rounded-2xl md:rounded-3xl overflow-hidden shadow-card aspect-[16/9] sm:aspect-auto sm:min-h-[190px] md:min-h-[220px] group`}>
                    <img src={s.img} alt={`${s.title} in Hanau – koordiniert von ManageMatch`} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/10 to-transparent" />
                    <div className="absolute bottom-3 left-3 right-3 md:bottom-4 md:left-4 md:right-4 glass-card rounded-xl md:rounded-2xl p-3 md:p-4 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl gradient-mint flex items-center justify-center shrink-0">
                        <s.icon className="w-5 h-5 text-primary-foreground" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground">{s.title}</h3>
                        <p className="text-sm text-muted-foreground">{s.description}</p>
                      </div>
                      <ArrowUpRight className="w-5 h-5 text-primary group-hover:rotate-45 transition-transform" />
                    </div>
                  </Link>
                ) : (
                  <Link key={s.title} to={s.to} className={`${s.span} ${s.bg} rounded-2xl md:rounded-3xl p-4 md:p-6 border border-border/40 flex flex-col justify-between min-h-[145px] md:min-h-[190px] group hover:-translate-y-1 transition-transform`}>
                    <div className="flex items-start justify-between">
                      <div className="w-11 h-11 rounded-xl bg-card/80 flex items-center justify-center">
                        <s.icon className="w-5 h-5 text-primary" />
                      </div>
                      <ArrowUpRight className="w-5 h-5 text-foreground/60 group-hover:rotate-45 transition-transform" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-foreground">{s.title}</h3>
                      <p className="text-sm text-muted-foreground">{s.description}</p>
                    </div>
                  </Link>
                )
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default ServicesSection;
